/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\BackupStorageConfigBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the maintenance module.
----------------------------------------------------------------*/


import { HardDrive } from 'lucide-react';
import { FormBox } from '../../../../components/ui/FormBox';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { useI18n } from '../../../../hooks/useI18n';

const SELECT_CLS = 'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

interface S3Config { bucket: string; region: string; accessKey: string; secretKey: string; }
interface FtpConfig { host: string; port: string; user: string; pass: string; path: string; }

interface Props {
    storageType: string;
    s3Config: S3Config;
    ftpConfig: FtpConfig;
    onStorageTypeChange: (v: string) => void;
    onS3Change: (updated: Partial<S3Config>) => void;
    onFtpChange: (updated: Partial<FtpConfig>) => void;
}

export function BackupStorageConfigBox({ storageType, s3Config, ftpConfig, onStorageTypeChange, onS3Change, onFtpChange }: Props) {
    const { t } = useI18n();

    return (
        <FormBox title={t('backupRestore:sections.storageConfig')} description={t('backupRestore:sections.storageConfigDesc')} icon={<HardDrive className="h-5 w-5 text-primary" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                <Label>{t('backupRestore:fields.storageType')}</Label>
                <select className={SELECT_CLS} value={storageType} onChange={(e) => onStorageTypeChange(e.target.value)}>
                    <option value="local">{t('backupRestore:fields.localFilesystem')}</option>
                    <option value="s3">{t('backupRestore:fields.s3ObjectStorage')}</option>
                    <option value="ftp">{t('backupRestore:fields.ftp')}</option>
                </select>

                {storageType === 'local' && <div className="col-span-2" />}

                {storageType === 's3' && (
                    <>
                        <Label>{t('backupRestore:fields.s3Bucket')}</Label>
                        <Input value={s3Config.bucket} onChange={(e) => onS3Change({ bucket: e.target.value })} />
                        <Label>{t('backupRestore:fields.region')}</Label>
                        <Input value={s3Config.region} onChange={(e) => onS3Change({ region: e.target.value })} />
                        <Label>{t('backupRestore:fields.accessKey')}</Label>
                        <Input value={s3Config.accessKey} onChange={(e) => onS3Change({ accessKey: e.target.value })} />
                        <Label>{t('backupRestore:fields.secretKey')}</Label>
                        <Input type="password" value={s3Config.secretKey} onChange={(e) => onS3Change({ secretKey: e.target.value })} />
                    </>
                )}

                {storageType === 'ftp' && (
                    <>
                        <Label>{t('backupRestore:fields.host')}</Label>
                        <Input value={ftpConfig.host} onChange={(e) => onFtpChange({ host: e.target.value })} />
                        <Label>{t('backupRestore:fields.port')}</Label>
                        <Input value={ftpConfig.port} onChange={(e) => onFtpChange({ port: e.target.value })} />
                        <Label>{t('backupRestore:fields.user')}</Label>
                        <Input value={ftpConfig.user} onChange={(e) => onFtpChange({ user: e.target.value })} />
                        <Label>{t('backupRestore:fields.password')}</Label>
                        <Input type="password" value={ftpConfig.pass} onChange={(e) => onFtpChange({ pass: e.target.value })} />
                        <Label>{t('backupRestore:fields.path')}</Label>
                        <Input value={ftpConfig.path} onChange={(e) => onFtpChange({ path: e.target.value })} />
                        <div className="col-span-2" />
                    </>
                )}
            </div>
        </FormBox>
    );
}
